import type Stripe from "stripe";
import { getGrantedEntitlementsFromPriceIds } from "@/lib/billing/entitlements";
import { findMinimumBundleForEntitlements, type BundleTierId } from "@/lib/billing/bundle-entitlements";
import { getCanonicalSubscriptionSlugForPriceId } from "@/utils/stripe/subscription-slugs";

export type BillingInterval = "month" | "year";

export type PlanSubscription = {
  priceId: string;
  status: string;
  interval: string | null;
  trialEnd: number | null;
  cancelAt: number | null;
  cancelAtPeriodEnd: boolean;
};

export type PurchaseRequest = { priceId: string; interval: BillingInterval };

export type PurchaseRoute =
  | { kind: "checkout" }
  | { kind: "included" }
  | { kind: "fix_billing" }
  | { kind: "plan_change"; targetTier: BundleTierId; targetInterval: BillingInterval; billingOnly: boolean };

export type BlockedPurchaseRoute = Exclude<PurchaseRoute, { kind: "checkout" }>;
export type PlanChangeRoute = Extract<PurchaseRoute, { kind: "plan_change" }>;

type SubscriptionRowLike = {
  price_id: string;
  status: string;
  subscription_interval?: string | null;
  period_end?: string | null;
  cancel_at?: string | null;
  cancel_at_period_end?: boolean | null;
};

const PLAN_STATUSES = new Set(["active", "trialing"]);
export const NEEDS_BILLING_STATUSES = new Set(["past_due", "unpaid", "incomplete", "paused"]);
const VIP_DISCORD_HOLDER_SLUGS = new Set(["diamond", "vip-discord"]);
export const BUNDLE_TIER_NAME: Record<BundleTierId, string> = { silver: "Silver", gold: "Gold", diamond: "Diamond" };

export const SIGN_IN_TO_CHANGE_PLAN = {
  error: "Sign in required",
  details: "This email already has a plan. Sign in to change it instead of starting a second subscription.",
} as const;

export function planChangeLabel(route: PlanChangeRoute): string {
  const plan = `${BUNDLE_TIER_NAME[route.targetTier]}${route.targetInterval === "year" ? " yearly" : ""}`;
  return route.billingOnly ? `Switch to ${plan}` : `Upgrade to ${plan}`;
}

export function purchaseRouteMessage(route: BlockedPurchaseRoute): string {
  switch (route.kind) {
    case "included":
      return "Your current plan already includes this.";
    case "fix_billing":
      return "Fix the payment on your current plan before changing it.";
    case "plan_change":
      return route.billingOnly
        ? `Your current plan already includes this. ${planChangeLabel(route)} instead of starting a second subscription.`
        : `You already have a plan. Upgrade it to ${BUNDLE_TIER_NAME[route.targetTier]} instead of starting a second subscription.`;
  }
}

export function endsWithoutCharge(sub: PlanSubscription): boolean {
  if (sub.status !== "trialing") return false;
  if (sub.cancelAtPeriodEnd) return true;
  return sub.cancelAt != null && sub.trialEnd != null && sub.cancelAt <= sub.trialEnd;
}

export function grantsTool(priceId: string): boolean {
  return getGrantedEntitlementsFromPriceIds([priceId]).size > 0;
}

export function isLivePlan(sub: PlanSubscription): boolean {
  return PLAN_STATUSES.has(sub.status) && !endsWithoutCharge(sub) && grantsTool(sub.priceId);
}

function needsBilling(sub: PlanSubscription): boolean {
  return NEEDS_BILLING_STATUSES.has(sub.status);
}

export function memberHoldsPlan(subscriptions: readonly PlanSubscription[]): boolean {
  return subscriptions.some((sub) => needsBilling(sub) || isLivePlan(sub));
}

function holdsVipDiscord(subscriptions: readonly PlanSubscription[]): boolean {
  return subscriptions.some(
    (sub) =>
      PLAN_STATUSES.has(sub.status) &&
      VIP_DISCORD_HOLDER_SLUGS.has(getCanonicalSubscriptionSlugForPriceId(sub.priceId) ?? ""),
  );
}

export function routePurchase(
  subscriptions: readonly PlanSubscription[],
  request: PurchaseRequest,
): PurchaseRoute {
  if (subscriptions.some(needsBilling)) return { kind: "fix_billing" };
  if (getCanonicalSubscriptionSlugForPriceId(request.priceId) === "vip-discord") {
    return holdsVipDiscord(subscriptions) ? { kind: "included" } : { kind: "checkout" };
  }

  const requested = getGrantedEntitlementsFromPriceIds([request.priceId]);
  const plans = subscriptions.filter(isLivePlan);
  if (requested.size === 0 || plans.length === 0) return { kind: "checkout" };

  const current = getGrantedEntitlementsFromPriceIds(plans.map((sub) => sub.priceId));
  const currentInterval: BillingInterval = plans.some((sub) => sub.interval === "year") ? "year" : "month";
  const targetInterval: BillingInterval = request.interval === "year" ? "year" : currentInterval;

  const billingOnly = [...requested].every((key) => current.has(key));
  if (billingOnly && targetInterval === currentInterval) return { kind: "included" };

  const targetTier = findMinimumBundleForEntitlements(new Set([...current, ...requested])) ?? "diamond";
  return { kind: "plan_change", targetTier, targetInterval, billingOnly };
}

export function planSubscriptionFromStripe(sub: Stripe.Subscription): PlanSubscription {
  const price = sub.items?.data?.[0]?.price;
  return {
    priceId: price?.id ?? "",
    status: sub.status,
    interval: price?.recurring?.interval ?? null,
    trialEnd: sub.trial_end ?? null,
    cancelAt: sub.cancel_at ?? null,
    cancelAtPeriodEnd: sub.cancel_at_period_end ?? false,
  };
}

function unixFromIso(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

export function planSubscriptionFromRow(row: SubscriptionRowLike): PlanSubscription {
  return {
    priceId: row.price_id,
    status: row.status,
    interval: row.subscription_interval ?? null,
    trialEnd: row.status === "trialing" ? unixFromIso(row.period_end) : null,
    cancelAt: unixFromIso(row.cancel_at),
    cancelAtPeriodEnd: Boolean(row.cancel_at_period_end),
  };
}
